/**
 * Cálculo de la "letra bucket" para el índice alfabético de la biblioteca.
 *
 * Dado un título, devuelve la letra A-Z bajo la que debería agruparse, o
 * `'#'` si no empieza por una letra latina (números, símbolos, otros
 * alfabetos). Pensado para alimentar el filtro `alphaFilter` del store de
 * biblioteca y los botones del `AlphabetBar`.
 *
 * Reglas (coinciden con lo que un usuario espera de un índice alfabético):
 *   1. Ignora artículos iniciales (es: El/La/Los/Las/Un/Una/Unos/Unas,
 *      en: The/A/An), case-insensitive. → "El Quijote" → Q.
 *   2. Pliega acentos/diacríticos vía NFD. → "Ávila" → A, "Ñandú" → N.
 *   3. Toma la primera letra en mayúsculas; si no es A-Z → '#'.
 *   4. Vacío / null / undefined → '#'.
 */

// El `$` permite que un título que es SOLO el artículo ("El", "The") también
// se trate como sin-letra-significativa → '#'.
const ARTICLES = /^(el|la|los|las|un|una|unos|unas|the|a|an)(?:\s+|$)/i

/**
 * Letra bucket A-Z (o '#') para un título.
 *
 * Exportada como función pura para poder testearla aislada y reutilizarla
 * tanto en el filtro del store como en el cómputo de letras disponibles.
 */
export function alphaBucket(title: string | null | undefined): string {
  // 0. Vacío/nulo → '#' (sin trabajo).
  const raw = (title ?? '').trim()
  if (!raw) return '#'
  // 1. Quitar artículo inicial (con espacio o fin de cadena detrás) y trimear.
  //    Si tras quitarlo no queda nada ("El" aislado), es '#'.
  const stripped = raw.replace(ARTICLES, '').trim()
  if (!stripped) return '#'
  // 2. Plegar acentos: NFD separa base + diacrítico; los diacríticos viven
  //    en el bloque U+0300–U+036F.
  const folded = stripped.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  // 3. Primera letra, mayúsculas.
  const ch = folded[0]?.toUpperCase() ?? ''
  return /^[A-Z]$/.test(ch) ? ch : '#'
}
